import React, { useState } from 'react';
import { Search, ArrowLeft, ChevronRight, Package, AlertTriangle, Sparkles, CheckCircle2, XCircle, Clock, MapPin, Truck, UserCircle2, Send, Phone, Mail, ArrowUpRight, Filter, Zap } from 'lucide-react';
type AnomalyStatus = 'Aperta' | 'In risoluzione' | 'Risolta' | 'Ignorata';
type AnomalyPriority = 'Alta' | 'Media' | 'Bassa';
type AnomalyType = 'Indirizzo' | 'Orario consegna' | 'Pacco danneggiato' | 'Consegna fallita' | 'Ritardo' | 'Documento mancante' | 'Discrepanza colli';
type ActivityType = 'detected' | 'ai-suggested' | 'note' | 'resolved' | 'rejected' | 'reassigned';
type Activity = {
  date: string;
  timestamp: string;
  author: string;
  description: string;
  type: ActivityType;
};
type AiSuggestion = {
  summary: string;
  actionLabel: string;
  confidence: number;
  rationale: string;
};
type Anomaly = {
  id: string;
  type: AnomalyType;
  title: string;
  description: string;
  shipmentRef: string;
  store: string;
  city: string;
  carrier: string;
  priority: AnomalyPriority;
  status: AnomalyStatus;
  reportedAt: string;
  resolvedAutomatically?: boolean;
  aiSuggestion?: AiSuggestion;
  activity: Activity[];
};
const CARRIER_LOGOS: Record<string, string> = {
  BRT: 'https://cdn.magicpatterns.com/uploads/cmtJEf49a5eVZLpdSNFSih/BRT_logo_cropped.png',
  FedEx: 'https://cdn.magicpatterns.com/uploads/bJFTTvq6RiT6tx6MWd19DM/FedEx-Logo.png',
  GLS: 'https://cdn.magicpatterns.com/uploads/kLsfczSQhw6HQ17cu2iXe1/GLS_Logo_2021.svg.png',
  SDA: 'https://cdn.magicpatterns.com/uploads/uzuZLxLhuWymtodu3YDZcY/1620120317-sda-poste-logo.webp',
  UPS: 'https://cdn.magicpatterns.com/uploads/5fMcthEgdAwTbqPzFRcjsj/logo_corriere_ups_500.png'
};
const ANOMALIES: Anomaly[] = [{
  id: 'ANM-2840-0218',
  type: 'Orario consegna',
  title: 'Consegna in pausa pranzo: negozio chiuso',
  description: 'Il vettore FedEx ha programmato la consegna alle 13:30 ma il negozio è chiuso dalle 13:00 alle 15:30. Rischio di consegna fallita.',
  shipmentRef: 'SH-7H2K9P',
  store: 'Store Milano Duomo',
  city: 'Milano',
  carrier: 'FedEx',
  priority: 'Alta',
  status: 'Aperta',
  reportedAt: '10 mag 07:42',
  aiSuggestion: {
    summary: 'Comunica al vettore la fascia oraria di ricezione del negozio (09:30–13:00 / 15:30–19:30) e riprogramma la consegna per oggi pomeriggio dopo le 15:30.',
    actionLabel: 'Riprogramma con FedEx',
    confidence: 94,
    rationale: 'Aisac ha confrontato gli orari del vettore con la scheda negozio. La fascia 15:30–17:00 è quella con maggiore tasso di firma per questo punto vendita.'
  },
  activity: [{
    date: '10 mag',
    timestamp: '07:43',
    author: 'Aisac AI',
    description: 'Suggerimento generato: riprogrammare consegna dopo le 15:30',
    type: 'ai-suggested'
  }, {
    date: '10 mag',
    timestamp: '07:42',
    author: 'Aisac AI',
    description: 'Anomalia rilevata su slot consegna BRT',
    type: 'detected'
  }]
}, {
  id: 'ANM-2840-0217',
  type: 'Indirizzo',
  title: 'Indirizzo di consegna incompleto',
  description: "L'indirizzo di consegna riporta 'Via del Corso, Milano' senza numero civico. Il vettore richiede l'integrazione per procedere.",
  shipmentRef: 'SH-7H2K9P',
  store: 'Store Milano Duomo',
  city: 'Milano',
  carrier: 'FedEx',
  priority: 'Alta',
  status: 'In risoluzione',
  reportedAt: '10 mag 06:35',
  aiSuggestion: {
    summary: "Integra automaticamente l'indirizzo con quello presente in anagrafica negozio: 'Galleria Vittorio Emanuele II, 12, 20121 Milano'.",
    actionLabel: 'Applica indirizzo completo',
    confidence: 98,
    rationale: "Aisac ha trovato corrispondenza univoca nell'anagrafica negozi e nelle ultime 24 consegne consegnate con successo allo stesso indirizzo."
  },
  activity: [{
    date: '10 mag',
    timestamp: '08:12',
    author: 'Marco Rossi',
    description: 'Anomalia presa in carico',
    type: 'note'
  }, {
    date: '10 mag',
    timestamp: '06:36',
    author: 'Aisac AI',
    description: 'Suggerimento generato dal database anagrafica',
    type: 'ai-suggested'
  }, {
    date: '10 mag',
    timestamp: '06:35',
    author: 'Aisac AI',
    description: 'Anomalia rilevata su indirizzo incompleto',
    type: 'detected'
  }]
}, {
  id: 'ANM-2840-0216',
  type: 'Ritardo',
  title: 'Spedizione in ritardo di 48 ore',
  description: 'La spedizione doveva essere consegnata il 09 mag ma è ancora in transito presso Hub Bari. Maltempo segnalato sulla tratta.',
  shipmentRef: 'SH-5J7L8X',
  store: 'Store Bari Mungivacca',
  city: 'Bari',
  carrier: 'UPS',
  priority: 'Media',
  status: 'Aperta',
  reportedAt: '10 mag 09:00',
  aiSuggestion: {
    summary: 'Notifica il negozio della nuova data di consegna (11 mag) e invia comunicazione al vettore per richiedere priorità sulla tratta.',
    actionLabel: 'Notifica negozio e vettore',
    confidence: 86,
    rationale: 'Su 7 ritardi simili negli ultimi 30 giorni, la notifica preventiva ha ridotto i reclami del 62%.'
  },
  activity: [{
    date: '10 mag',
    timestamp: '09:01',
    author: 'Aisac AI',
    description: 'Suggerimento generato per gestione ritardo',
    type: 'ai-suggested'
  }, {
    date: '10 mag',
    timestamp: '09:00',
    author: 'Aisac AI',
    description: 'Anomalia rilevata: scarto ETA > 24h',
    type: 'detected'
  }]
}, {
  id: 'ANM-2840-0215',
  type: 'Consegna fallita',
  title: 'Tentativo di consegna fallito',
  description: 'SDA ha tentato la consegna il 09 mag alle 11:15 ma il negozio era chiuso. Pacco rientrato in deposito.',
  shipmentRef: 'SH-8P1S3W',
  store: 'Store Catania Etnapolis',
  city: 'Catania',
  carrier: 'SDA',
  priority: 'Alta',
  status: 'Aperta',
  reportedAt: '09 mag 11:20',
  aiSuggestion: {
    summary: 'Verifica gli orari di apertura del negozio nel C.C. Etnapolis (apre alle 10:00 nei feriali) e riprogramma la consegna per il 11 mag mattina.',
    actionLabel: 'Riprogramma consegna',
    confidence: 91,
    rationale: 'Le ultime 3 consegne andate a buon fine in questo punto vendita sono avvenute tra le 10:30 e le 12:00.'
  },
  activity: [{
    date: '09 mag',
    timestamp: '11:21',
    author: 'Aisac AI',
    description: 'Suggerimento generato per riprogrammazione',
    type: 'ai-suggested'
  }, {
    date: '09 mag',
    timestamp: '11:20',
    author: 'Aisac AI',
    description: 'Anomalia rilevata: consegna fallita SDA',
    type: 'detected'
  }]
}, {
  id: 'ANM-2840-0214',
  type: 'Discrepanza colli',
  title: 'Colli ricevuti diversi da spediti',
  description: 'Il negozio ha confermato la ricezione di 5 colli su 6 spediti. Il collo mancante risulta ancora in tracking.',
  shipmentRef: 'SH-4N2Z7H',
  store: 'Store Bologna Centro',
  city: 'Bologna',
  carrier: 'FedEx',
  priority: 'Media',
  status: 'In risoluzione',
  reportedAt: '08 mag 16:40',
  aiSuggestion: {
    summary: 'Apri una pratica di smarrimento con FedEx allegando DDT e foto ricezione. In parallelo riprepara la spedizione del collo mancante dal magazzino di Roma.',
    actionLabel: 'Apri pratica vettore',
    confidence: 78,
    rationale: "Pattern già visto su altre 4 spedizioni: nel 75% dei casi il collo viene ritrovato entro 48 ore, ma è opportuno preparare un'eventuale rispedizione."
  },
  activity: [{
    date: '09 mag',
    timestamp: '09:30',
    author: 'Giulia Conti',
    description: 'In contatto con FedEx per pratica di ricerca',
    type: 'note'
  }, {
    date: '08 mag',
    timestamp: '16:42',
    author: 'Aisac AI',
    description: 'Suggerimento generato per smarrimento collo',
    type: 'ai-suggested'
  }, {
    date: '08 mag',
    timestamp: '16:40',
    author: 'Aisac AI',
    description: 'Anomalia rilevata: discrepanza colli ricevuti',
    type: 'detected'
  }]
}, {
  id: 'ANM-2840-0213',
  type: 'Indirizzo',
  title: 'Indirizzo aggiornato automaticamente',
  description: 'Aisac ha corretto in autonomia un CAP errato sulla destinazione, evitando un mancato recapito.',
  shipmentRef: 'SH-6B9C5Y',
  store: 'Store Torino Lingotto',
  city: 'Torino',
  carrier: 'FedEx',
  priority: 'Bassa',
  status: 'Risolta',
  reportedAt: '09 mag 14:22',
  resolvedAutomatically: true,
  activity: [{
    date: '09 mag',
    timestamp: '14:23',
    author: 'Aisac AI',
    description: 'CAP corretto da 10100 a 10126 e notifica inviata a GLS',
    type: 'resolved'
  }, {
    date: '09 mag',
    timestamp: '14:22',
    author: 'Aisac AI',
    description: 'Anomalia rilevata: CAP non valido per Torino Lingotto',
    type: 'detected'
  }]
}, {
  id: 'ANM-2840-0212',
  type: 'Pacco danneggiato',
  title: 'Imballo segnalato come danneggiato dal vettore',
  description: 'Il vettore UPS ha segnalato un imballo lacerato durante il transito presso Hub Napoli. Foto allegata.',
  shipmentRef: 'SH-5J7L8X',
  store: 'Store Bari Mungivacca',
  city: 'Bari',
  carrier: 'UPS',
  priority: 'Media',
  status: 'Risolta',
  reportedAt: '08 mag 04:18',
  activity: [{
    date: '08 mag',
    timestamp: '10:14',
    author: 'Marco Rossi',
    description: 'Anomalia gestita: notificato negozio e aperta pratica vettore per eventuale reso',
    type: 'resolved'
  }, {
    date: '08 mag',
    timestamp: '04:19',
    author: 'Aisac AI',
    description: 'Suggerimento generato: notificare negozio destinatario',
    type: 'ai-suggested'
  }, {
    date: '08 mag',
    timestamp: '04:18',
    author: 'Aisac AI',
    description: 'Anomalia rilevata: imballo danneggiato segnalato da UPS',
    type: 'detected'
  }]
}, {
  id: 'ANM-2840-0211',
  type: 'Documento mancante',
  title: 'DDT non allegato alla spedizione',
  description: 'La spedizione non ha un DDT collegato. Possibile errore di importazione dal gestionale.',
  shipmentRef: 'SH-2K6V4T',
  store: 'Store Palermo La Torre',
  city: 'Palermo',
  carrier: 'FedEx',
  priority: 'Bassa',
  status: 'Ignorata',
  reportedAt: '05 mag 09:14',
  activity: [{
    date: '05 mag',
    timestamp: '09:30',
    author: 'Marco Rossi',
    description: 'Anomalia ignorata: DDT cartaceo viaggia insieme al pacco',
    type: 'rejected'
  }, {
    date: '05 mag',
    timestamp: '09:14',
    author: 'Aisac AI',
    description: 'Anomalia rilevata: DDT digitale mancante',
    type: 'detected'
  }]
}];
const FILTERS: {
  key: string;
  label: string;
}[] = [{
  key: 'Tutte',
  label: 'Tutte'
}, {
  key: 'Aperta',
  label: 'Aperte'
}, {
  key: 'In risoluzione',
  label: 'In risoluzione'
}, {
  key: 'Risolta',
  label: 'Risolte'
}, {
  key: 'Ignorata',
  label: 'Ignorate'
}];
const STATUS_STYLES: Record<AnomalyStatus, string> = {
  Aperta: 'bg-[#EF4444]/10 text-[#EF4444] border border-[#EF4444]/20',
  'In risoluzione': 'bg-[#F5A524]/10 text-[#F5A524] border border-[#F5A524]/20',
  Risolta: 'bg-[#D4FF3A]/10 text-[#D4FF3A] border border-[#D4FF3A]/30',
  Ignorata: 'bg-[#1F1F1F] text-[#9A9A9A] border border-[#2A2A2A]'
};
const PRIORITY_STYLES: Record<AnomalyPriority, string> = {
  Alta: 'bg-[#EF4444]/10 text-[#EF4444] border border-[#EF4444]/20',
  Media: 'bg-[#F5A524]/10 text-[#F5A524] border border-[#F5A524]/20',
  Bassa: 'bg-[#1F1F1F] text-[#9A9A9A] border border-[#2A2A2A]'
};
function StatusBadge({
  status
}: {
  status: AnomalyStatus;
}) {
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium ${STATUS_STYLES[status]}`} data-id="element-1113">
      {status}
    </span>;
}
function PriorityBadge({
  priority
}: {
  priority: AnomalyPriority;
}) {
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-medium uppercase tracking-wider ${PRIORITY_STYLES[priority]}`} data-id="element-1114">
      {priority}
    </span>;
}
function TypeBadge({
  type
}: {
  type: AnomalyType;
}) {
  return <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium bg-[#1F1F1F] text-[#FAFAFA] border border-[#2A2A2A]" data-id="element-1115">
      {type}
    </span>;
}
type AnomalieProps = {
  selectedId?: string | null;
  onSelectedIdChange?: (id: string | null) => void;
};
export function Anomalie({
  selectedId: controlledSelectedId,
  onSelectedIdChange
}: AnomalieProps = {}) {
  const [internalSelectedId, setInternalSelectedId] = useState<string | null>(null);
  const selectedId = controlledSelectedId !== undefined ? controlledSelectedId : internalSelectedId;
  const setSelectedId = (id: string | null) => {
    if (onSelectedIdChange) onSelectedIdChange(id);else setInternalSelectedId(id);
  };
  const [filter, setFilter] = useState('Tutte');
  const [query, setQuery] = useState('');
  const selected = ANOMALIES.find(a => a.id === selectedId) || null;
  if (selected) {
    return <AnomalyDetail item={selected} onBack={() => setSelectedId(null)} data-id="element-1116" />;
  }
  const filtered = ANOMALIES.filter(a => (filter === 'Tutte' || a.status === filter) && (query.trim() === '' || a.id.toLowerCase().includes(query.toLowerCase()) || a.title.toLowerCase().includes(query.toLowerCase()) || a.store.toLowerCase().includes(query.toLowerCase()) || a.shipmentRef.toLowerCase().includes(query.toLowerCase())));
  const openCount = ANOMALIES.filter(a => a.status === 'Aperta').length;
  const inProgressCount = ANOMALIES.filter(a => a.status === 'In risoluzione').length;
  const resolvedCount = ANOMALIES.filter(a => a.status === 'Risolta').length;
  const ignoredCount = ANOMALIES.filter(a => a.status === 'Ignorata').length;
  return <div className="space-y-6" data-id="element-1117">
      <h1 className="text-2xl font-semibold text-[#FAFAFA] font-heading tracking-tight" data-id="element-1118">
        Anomalie
      </h1>

      {/* Stat strip */}
      <section className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-1119">
        <StatCard label="Aperte" value={String(openCount)} icon={<AlertTriangle className="h-4 w-4 text-[#EF4444]" data-id="element-1121" />} data-id="element-1120" />
        <StatCard label="In risoluzione" value={String(inProgressCount)} icon={<Clock className="h-4 w-4 text-[#F5A524]" data-id="element-1123" />} data-id="element-1122" />
        <StatCard label="Risolte" value={String(resolvedCount)} icon={<CheckCircle2 className="h-4 w-4 text-[#D4FF3A]" data-id="element-1125" />} data-id="element-1124" />
        <StatCard label="Richieste ignorate" value={String(ignoredCount)} icon={<XCircle className="h-4 w-4 text-[#9A9A9A]" data-id="element-1127" />} data-id="element-1126" />
      </section>

      {/* Table */}
      <div className="bg-[#181818] border border-[#262626] rounded-xl" data-id="element-1128">
        <div className="p-5 border-b border-[#262626] flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-id="element-1129">
          <div className="-mx-4 sm:mx-0 px-4 sm:px-0 overflow-x-auto" data-id="element-1130">
            <div className="flex items-center gap-1 p-1 rounded-lg bg-[#0A0A0A] border border-[#1F1F1F] w-max sm:w-auto" data-id="element-1131">
              {FILTERS.map(f => <button key={f.key} onClick={() => setFilter(f.key)} className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors whitespace-nowrap ${filter === f.key ? 'bg-[#1F1F1F] text-[#FAFAFA]' : 'text-[#9A9A9A] hover:text-[#FAFAFA]'}`} data-id="element-1132">
                  {f.label}
                </button>)}
            </div>
          </div>

          <div className="relative" data-id="element-1133">
            <Search className="h-4 w-4 text-[#6B6B6B] absolute left-3 top-1/2 -translate-y-1/2" data-id="element-1134" />
            <input type="text" value={query} onChange={e => setQuery(e.target.value)} placeholder="Cerca per ID, titolo, negozio..." className="w-full sm:w-64 pl-9 pr-3 py-1.5 rounded-lg bg-[#0A0A0A] border border-[#1F1F1F] text-xs text-[#FAFAFA] placeholder:text-[#6B6B6B] focus:outline-none focus:ring-2 focus:ring-[#D4FF3A]/30" data-id="element-1135" />
          </div>
        </div>

        {/* Mobile card list */}
        <div className="md:hidden divide-y divide-[#1F1F1F]" data-id="element-1136">
          {filtered.map(a => <button key={a.id} onClick={() => setSelectedId(a.id)} className="w-full text-left p-4 active:bg-[#0A0A0A] transition-colors" data-id="element-1137">
              <div className="flex items-start justify-between gap-3 mb-2" data-id="element-1138">
                <div className="flex items-center gap-2 min-w-0" data-id="element-1139">
                  <span className="font-mono text-xs text-[#FAFAFA]" data-id="element-1140">
                    {a.id}
                  </span>
                  {a.resolvedAutomatically && <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-[#D4FF3A]/10 border border-[#D4FF3A]/30 text-[10px] font-medium text-[#D4FF3A]" data-id="element-1141">
                      <Sparkles className="h-2.5 w-2.5" data-id="element-1142" />
                      AI
                    </span>}
                </div>
                <StatusBadge status={a.status} data-id="element-1143" />
              </div>
              <div className="text-sm text-[#FAFAFA] font-medium leading-snug mb-2" data-id="element-1144">
                {a.title}
              </div>
              <div className="flex items-center gap-1.5 flex-wrap mb-2" data-id="element-1145">
                <TypeBadge type={a.type} data-id="element-1146" />
                <PriorityBadge priority={a.priority} data-id="element-1147" />
              </div>
              <div className="flex items-center justify-between gap-3 text-[11px] text-[#6B6B6B] font-mono" data-id="element-1148">
                <span data-id="element-1149">
                  {a.carrier} · {a.shipmentRef}
                </span>
                <span data-id="element-1150">{a.reportedAt}</span>
              </div>
            </button>)}
          {filtered.length === 0 && <div className="p-8 text-center text-sm text-[#6B6B6B]" data-id="element-1151">
              Nessuna anomalia corrisponde ai filtri.
            </div>}
        </div>

        {/* Desktop table */}
        <div className="hidden md:block overflow-x-auto" data-id="element-1152">
          <table className="w-full text-sm [&_th]:whitespace-nowrap [&_td]:whitespace-nowrap" data-id="element-1153">
            <thead data-id="element-1154">
              <tr className="text-left text-[10px] uppercase tracking-wider text-[#6B6B6B] border-b border-[#1F1F1F]" data-id="element-1155">
                <th className="px-5 py-3 font-medium" data-id="element-1156">Anomalia</th>
                <th className="px-5 py-3 font-medium" data-id="element-1157">Tipo</th>
                <th className="px-5 py-3 font-medium" data-id="element-1158">Spedizione / Negozio</th>
                <th className="px-5 py-3 font-medium" data-id="element-1159">Vettore</th>
                <th className="px-5 py-3 font-medium" data-id="element-1160">Priorità</th>
                <th className="px-5 py-3 font-medium" data-id="element-1161">Stato</th>
                <th className="px-5 py-3 font-medium" data-id="element-1162">Segnalata</th>
                <th className="px-5 py-3 font-medium w-8" data-id="element-1163" />
              </tr>
            </thead>
            <tbody data-id="element-1164">
              {filtered.map(a => <tr key={a.id} onClick={() => setSelectedId(a.id)} className="border-b border-[#1F1F1F] last:border-0 hover:bg-[#0A0A0A] transition-colors cursor-pointer" data-id="element-1165">
                  <td className="px-5 py-3.5" data-id="element-1166">
                    <div className="flex items-center gap-2" data-id="element-1167">
                      <div className="font-mono text-xs text-[#FAFAFA]" data-id="element-1168">
                        {a.id}
                      </div>
                      {a.resolvedAutomatically && <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-[#D4FF3A]/10 border border-[#D4FF3A]/30 text-[10px] font-medium text-[#D4FF3A]" title="Risolta automaticamente da Aisac AI" data-id="element-1169">
                          <Sparkles className="h-2.5 w-2.5" data-id="element-1170" />
                          AI
                        </span>}
                    </div>
                    <div className="text-[11px] text-[#9A9A9A] mt-0.5 truncate max-w-[260px]" data-id="element-1171">
                      {a.title}
                    </div>
                  </td>
                  <td className="px-5 py-3.5" data-id="element-1172">
                    <TypeBadge type={a.type} data-id="element-1173" />
                  </td>
                  <td className="px-5 py-3.5" data-id="element-1174">
                    <div className="text-[#FAFAFA] text-xs font-mono" data-id="element-1175">
                      {a.shipmentRef}
                    </div>
                    <div className="text-[11px] text-[#9A9A9A] mt-0.5 truncate max-w-[200px]" data-id="element-1176">
                      {a.store}
                    </div>
                  </td>
                  <td className="px-5 py-3.5" data-id="element-1177">
                    <div className="flex items-center gap-2" data-id="element-1178">
                      <div className="w-7 h-7 rounded bg-white flex items-center justify-center shrink-0 p-1" data-id="element-1179">
                        <img src={CARRIER_LOGOS[a.carrier]} alt={a.carrier} className="max-w-full max-h-full object-contain" data-id="element-1180" />
                      </div>
                      <span className="text-xs text-[#9A9A9A]" data-id="element-1181">
                        {a.carrier}
                      </span>
                    </div>
                  </td>
                  <td className="px-5 py-3.5" data-id="element-1182">
                    <PriorityBadge priority={a.priority} data-id="element-1183" />
                  </td>
                  <td className="px-5 py-3.5" data-id="element-1184">
                    <StatusBadge status={a.status} data-id="element-1185" />
                  </td>
                  <td className="px-5 py-3.5 text-[#9A9A9A] font-mono text-xs" data-id="element-1186">
                    {a.reportedAt}
                  </td>
                  <td className="px-5 py-3.5 text-right" data-id="element-1187">
                    <ChevronRight className="h-4 w-4 text-[#6B6B6B] inline" data-id="element-1188" />
                  </td>
                </tr>)}
              {filtered.length === 0 && <tr data-id="element-1189">
                  <td colSpan={8} className="px-5 py-10 text-center text-sm text-[#6B6B6B]" data-id="element-1190">
                    Nessuna anomalia corrisponde ai filtri.
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>;
}
function StatCard({
  label,
  value,
  icon,
  accent
}: {
  label: string;
  value: string;
  icon: React.ReactNode;
  accent?: boolean;
}) {
  return <div className={`bg-[#181818] border rounded-xl p-5 ${accent ? 'border-[#D4FF3A]/30' : 'border-[#262626]'}`} data-id="element-1191">
      <div className="flex items-center justify-between mb-3" data-id="element-1192">
        <span className="text-xs text-[#9A9A9A] font-medium" data-id="element-1193">{label}</span>
        {icon}
      </div>
      <div className="text-2xl font-semibold font-heading tracking-tight text-[#FAFAFA]" data-id="element-1194">
        {value}
      </div>
    </div>;
}
function AnomalyDetail({
  item,
  onBack
}: {
  item: Anomaly;
  onBack: () => void;
}) {
  const [status, setStatus] = useState<AnomalyStatus>(item.status);
  const [applied, setApplied] = useState(false);
  const [note, setNote] = useState('');
  const isOpen = status === 'Aperta' || status === 'In risoluzione';
  const handleApply = () => {
    setApplied(true);
    setStatus('Risolta');
  };
  const handleIgnore = () => setStatus('Ignorata');
  const handleResolve = () => setStatus('Risolta');
  return <div className="space-y-6" data-id="element-1195">
      <button onClick={onBack} className="inline-flex items-center gap-1.5 text-sm text-[#9A9A9A] hover:text-[#FAFAFA] transition-colors" data-id="element-1196">
        <ArrowLeft className="h-4 w-4" data-id="element-1197" />
        <span data-id="element-1198">Torna alle anomalie</span>
      </button>

      {/* Header */}
      <div className="bg-[#181818] border border-[#262626] rounded-xl p-4 sm:p-5" data-id="element-1199">
        <div className="flex items-start justify-between gap-4 flex-wrap" data-id="element-1200">
          <div className="flex items-start gap-4" data-id="element-1201">
            <div className="h-12 w-12 rounded-lg bg-[#EF4444]/10 border border-[#EF4444]/30 flex items-center justify-center shrink-0" data-id="element-1202">
              <AlertTriangle className="h-5 w-5 text-[#EF4444]" data-id="element-1203" />
            </div>
            <div className="min-w-0" data-id="element-1204">
              <div className="flex items-center gap-2 mb-1 flex-wrap" data-id="element-1205">
                <span className="text-xs font-mono text-[#9A9A9A]" data-id="element-1206">
                  {item.id}
                </span>
                <span className="text-[#6B6B6B]" data-id="element-1207">•</span>
                <span className="text-xs font-mono text-[#9A9A9A]" data-id="element-1208">
                  Spedizione {item.shipmentRef}
                </span>
              </div>
              <h2 className="text-lg font-semibold text-[#FAFAFA] font-heading" data-id="element-1209">
                {item.title}
              </h2>
              <p className="text-sm text-[#9A9A9A] mt-1 leading-relaxed max-w-2xl" data-id="element-1210">
                {item.description}
              </p>
              <div className="flex items-center gap-2 mt-3 flex-wrap" data-id="element-1211">
                <TypeBadge type={item.type} data-id="element-1212" />
                <PriorityBadge priority={item.priority} data-id="element-1213" />
                <StatusBadge status={status} data-id="element-1214" />
                {item.resolvedAutomatically && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-[#D4FF3A]/10 border border-[#D4FF3A]/30 text-[10px] font-medium uppercase tracking-wider text-[#D4FF3A]" data-id="element-1215">
                    <Sparkles className="h-3 w-3" data-id="element-1216" />
                    Risolta da Aisac
                  </span>}
              </div>
            </div>
          </div>
          <div className="text-right" data-id="element-1217">
            <div className="text-[10px] uppercase tracking-wider text-[#6B6B6B] font-medium" data-id="element-1218">
              Segnalata
            </div>
            <div className="text-sm font-semibold text-[#FAFAFA] font-mono mt-1" data-id="element-1219">
              {item.reportedAt}
            </div>
          </div>
        </div>
      </div>

      {/* AI suggestion */}
      {item.aiSuggestion && <div className="relative rounded-xl border border-[#D4FF3A]/30 bg-gradient-to-br from-[#181818] to-[#1a1d10] p-5 overflow-hidden" data-id="element-1220">
          <div className="absolute top-0 right-0 h-px w-32 bg-gradient-to-r from-transparent via-[#D4FF3A]/40 to-transparent" data-id="element-1221" />

          <div className="flex items-start gap-3 mb-4" data-id="element-1222">
            <div className="h-9 w-9 rounded-lg bg-[#D4FF3A]/10 border border-[#D4FF3A]/30 text-[#D4FF3A] flex items-center justify-center shrink-0" data-id="element-1223">
              <Sparkles className="h-[18px] w-[18px]" strokeWidth={2} data-id="element-1224" />
            </div>
            <div className="flex-1 min-w-0" data-id="element-1225">
              <div className="flex items-center gap-2 flex-wrap" data-id="element-1226">
                <span className="text-sm font-semibold text-[#FAFAFA] font-heading" data-id="element-1227">
                  Suggerimento Aisac AI
                </span>
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-[#D4FF3A]/10 border border-[#D4FF3A]/30 text-[10px] font-medium text-[#D4FF3A]" data-id="element-1228">
                  <Zap className="h-2.5 w-2.5" data-id="element-1229" />
                  Confidenza {item.aiSuggestion.confidence}%
                </span>
              </div>
              <p className="text-sm text-[#FAFAFA] mt-2 leading-relaxed" data-id="element-1230">
                {item.aiSuggestion.summary}
              </p>
              <p className="text-xs text-[#9A9A9A] mt-2 leading-relaxed" data-id="element-1231">
                <span className="text-[#6B6B6B]" data-id="element-1232">Perché:</span>{' '}
                {item.aiSuggestion.rationale}
              </p>
            </div>
          </div>

          {!applied && isOpen ? <div className="flex items-center gap-2 flex-wrap" data-id="element-1233">
              <button onClick={handleApply} className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-[#D4FF3A] text-[#0A0A0A] text-sm font-semibold hover:bg-[#c2ed28] focus:outline-none focus:ring-2 focus:ring-[#D4FF3A]/40 transition" data-id="element-1234">
                <CheckCircle2 className="h-4 w-4" data-id="element-1235" />
                {item.aiSuggestion.actionLabel}
              </button>
              <button className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-[#181818] border border-[#262626] text-[#9A9A9A] hover:text-[#FAFAFA] hover:border-[#333] text-sm font-medium transition" data-id="element-1236">
                Modifica prima di applicare
              </button>
            </div> : applied ? <div className="flex items-center gap-2 text-sm text-[#D4FF3A]" data-id="element-1237">
              <CheckCircle2 className="h-4 w-4" data-id="element-1238" />
              <span data-id="element-1239">
                Suggerimento applicato. Aisac ha notificato il vettore.
              </span>
            </div> : null}
        </div>}

      {/* Related shipment + actions */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-id="element-1240">
        <div className="lg:col-span-2 bg-[#181818] border border-[#262626] rounded-xl p-5" data-id="element-1241">
          <h3 className="text-sm font-semibold text-[#FAFAFA] font-heading mb-3" data-id="element-1242">
            Spedizione collegata
          </h3>
          <div className="flex items-start justify-between gap-4 flex-wrap" data-id="element-1243">
            <div className="flex items-center gap-3" data-id="element-1244">
              <div className="h-10 w-12 rounded-lg bg-white flex items-center justify-center shrink-0 p-1.5" data-id="element-1245">
                <img src={CARRIER_LOGOS[item.carrier]} alt={item.carrier} className="max-w-full max-h-full object-contain" data-id="element-1246" />
              </div>
              <div data-id="element-1247">
                <div className="text-xs font-mono text-[#9A9A9A] flex items-center gap-1.5" data-id="element-1248">
                  <Package className="h-3 w-3" data-id="element-1249" />
                  {item.shipmentRef}
                </div>
                <div className="text-sm font-medium text-[#FAFAFA] mt-0.5" data-id="element-1250">
                  {item.store}
                </div>
                <div className="text-xs text-[#9A9A9A] mt-0.5 flex items-center gap-1.5" data-id="element-1251">
                  <MapPin className="h-3 w-3" data-id="element-1252" />
                  Roma → {item.city}
                </div>
              </div>
            </div>
            <button className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#0A0A0A] border border-[#262626] text-xs font-medium text-[#FAFAFA] hover:border-[#333] transition" data-id="element-1253">
              <span data-id="element-1254">Apri spedizione</span>
              <ArrowUpRight className="h-3 w-3" data-id="element-1255" />
            </button>
          </div>
        </div>
        <div className="bg-[#181818] border border-[#262626] rounded-xl p-5 space-y-3" data-id="element-1256">
          <h3 className="text-sm font-semibold text-[#FAFAFA] font-heading" data-id="element-1257">
            Contatti rapidi
          </h3>
          <div className="space-y-2" data-id="element-1258">
            <button className="w-full inline-flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-[#0A0A0A] border border-[#262626] text-xs text-[#FAFAFA] hover:border-[#333] transition" data-id="element-1259">
              <span className="flex items-center gap-2" data-id="element-1260">
                <Phone className="h-3.5 w-3.5 text-[#9A9A9A]" data-id="element-1261" />
                Chiama il negozio
              </span>
              <ArrowUpRight className="h-3 w-3 text-[#6B6B6B]" data-id="element-1262" />
            </button>
            <button className="w-full inline-flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-[#0A0A0A] border border-[#262626] text-xs text-[#FAFAFA] hover:border-[#333] transition" data-id="element-1263">
              <span className="flex items-center gap-2" data-id="element-1264">
                <Mail className="h-3.5 w-3.5 text-[#9A9A9A]" data-id="element-1265" />
                Contatta {item.carrier}
              </span>
              <ArrowUpRight className="h-3 w-3 text-[#6B6B6B]" data-id="element-1266" />
            </button>
          </div>
        </div>
      </div>

      {/* Actions */}
      {isOpen && <div className="bg-[#181818] border border-[#262626] rounded-xl p-5" data-id="element-1267">
          <h3 className="text-sm font-semibold text-[#FAFAFA] font-heading mb-3" data-id="element-1268">
            Aggiungi una nota
          </h3>
          <form onSubmit={e => {
        e.preventDefault();
        setNote('');
      }} className="space-y-3" data-id="element-1269">
            <textarea value={note} onChange={e => setNote(e.target.value)} rows={3} placeholder="Scrivi un aggiornamento, una decisione o un follow-up..." className="w-full px-3 py-2.5 rounded-lg bg-[#0A0A0A] border border-[#262626] text-sm text-[#FAFAFA] placeholder:text-[#6B6B6B] focus:outline-none focus:ring-2 focus:ring-[#D4FF3A]/30 focus:border-[#333] resize-none" data-id="element-1270" />
            <div className="flex items-center justify-between gap-3 flex-wrap" data-id="element-1271">
              <div className="flex items-center gap-2" data-id="element-1272">
                <button type="submit" disabled={!note.trim()} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#1F1F1F] border border-[#262626] text-sm font-medium text-[#FAFAFA] hover:bg-[#222] disabled:opacity-50 disabled:cursor-not-allowed transition" data-id="element-1273">
                  <Send className="h-3.5 w-3.5" data-id="element-1274" />
                  Aggiungi nota
                </button>
              </div>
              <div className="flex items-center gap-2 flex-wrap" data-id="element-1275">
                <button type="button" onClick={handleResolve} className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-[#D4FF3A] text-[#0A0A0A] text-sm font-semibold hover:bg-[#c2ed28] transition" data-id="element-1276">
                  <CheckCircle2 className="h-4 w-4" data-id="element-1277" />
                  Segna come risolta
                </button>
                <button type="button" onClick={handleIgnore} className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-[#181818] border border-[#262626] text-[#9A9A9A] hover:text-[#FAFAFA] hover:border-[#333] text-sm font-medium transition" data-id="element-1278">
                  <XCircle className="h-4 w-4" data-id="element-1279" />
                  Ignora
                </button>
              </div>
            </div>
          </form>
        </div>}

      {/* Activity log */}
      <div className="bg-[#181818] border border-[#262626] rounded-xl" data-id="element-1280">
        <div className="flex items-center justify-between p-5 pb-4" data-id="element-1281">
          <h3 className="text-sm font-semibold text-[#FAFAFA] font-heading" data-id="element-1282">
            Cronologia
          </h3>
          <span className="text-[10px] uppercase tracking-wider text-[#6B6B6B] font-medium flex items-center gap-1.5" data-id="element-1283">
            <Clock className="h-3 w-3" data-id="element-1284" />
            {item.activity.length} eventi
          </span>
        </div>
        <div className="px-5 pb-5" data-id="element-1285">
          <ol className="relative space-y-1" data-id="element-1286">
            {item.activity.map((event, idx) => {
            const isLast = idx === item.activity.length - 1;
            const isAi = event.author === 'Aisac AI';
            const Icon = event.type === 'detected' ? AlertTriangle : event.type === 'ai-suggested' ? Sparkles : event.type === 'resolved' ? CheckCircle2 : event.type === 'rejected' ? XCircle : event.type === 'reassigned' ? UserCircle2 : Truck;
            const colorClass = event.type === 'detected' ? 'bg-[#EF4444]/10 border-[#EF4444]/30 text-[#EF4444]' : event.type === 'ai-suggested' ? 'bg-[#D4FF3A]/10 border-[#D4FF3A]/30 text-[#D4FF3A]' : event.type === 'resolved' ? 'bg-[#D4FF3A]/10 border-[#D4FF3A]/30 text-[#D4FF3A]' : event.type === 'rejected' ? 'bg-[#1F1F1F] border-[#2A2A2A] text-[#9A9A9A]' : 'bg-[#0A0A0A] border-[#262626] text-[#9A9A9A]';
            return <li key={idx} className="relative flex gap-3 pb-4 last:pb-0" data-id="element-1287">
                  {!isLast && <span className="absolute left-[15px] top-8 bottom-0 w-px bg-[#262626]" data-id="element-1288" />}
                  <div className={`relative z-10 h-8 w-8 rounded-lg border flex items-center justify-center shrink-0 ${colorClass}`} data-id="element-1289">
                    <Icon className="h-4 w-4" strokeWidth={2} data-id="element-1290" />
                  </div>
                  <div className="flex-1 min-w-0 pt-0.5" data-id="element-1291">
                    <div className="text-sm text-[#FAFAFA] font-medium" data-id="element-1292">
                      {event.description}
                    </div>
                    <div className="flex items-center gap-2 mt-0.5 text-[11px] text-[#6B6B6B]" data-id="element-1293">
                      <span className={`font-medium ${isAi ? 'text-[#D4FF3A]' : 'text-[#9A9A9A]'}`} data-id="element-1294">
                        {event.author}
                      </span>
                      <span data-id="element-1295">•</span>
                      <span className="font-mono" data-id="element-1296">{event.date}</span>
                      <span data-id="element-1297">•</span>
                      <span className="font-mono" data-id="element-1298">{event.timestamp}</span>
                    </div>
                  </div>
                </li>;
          })}
          </ol>
        </div>
      </div>
    </div>;
}